import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { Button, IconButton, Input, Select, Switch, useToast } from '@/components/ui'

export interface StepField {
  id: string
  step_id: string
  label: string
  field_key: string
  field_type: string
  is_required: number
  options: Record<string, unknown> | null
  default_value: string | null
  sort_order: number
}

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Mehrzeilig' },
  { value: 'number', label: 'Zahl' },
  { value: 'date', label: 'Datum' },
  { value: 'datetime', label: 'Datum & Zeit' },
  { value: 'select', label: 'Auswahl' },
  { value: 'multiselect', label: 'Mehrfachauswahl' },
  { value: 'checkbox', label: 'Ja/Nein' },
  { value: 'file', label: 'Datei' },
  { value: 'signature', label: 'Unterschrift' },
]

interface FieldsManagerProps {
  templateId: string
  stepId: string
}

export function FieldsManager({ templateId, stepId }: FieldsManagerProps) {
  const { toast } = useToast()
  const base = `/prozesse/templates/${templateId}/steps/${stepId}/fields`
  const { data: fields, refetch } = useQuery<StepField[]>({
    queryKey: ['proc-step-fields', stepId],
    queryFn: () => apiClient.get<StepField[]>(base).then((r) => r.data),
  })

  const [label, setLabel] = useState('')
  const [fieldKey, setFieldKey] = useState('')
  const [fieldType, setFieldType] = useState('text')
  const [required, setRequired] = useState(false)
  const [choices, setChoices] = useState('')
  const [saving, setSaving] = useState(false)

  async function addField() {
    if (!label.trim() || !fieldKey.trim()) {
      toast({ title: 'Bezeichnung und Schlüssel sind erforderlich', tone: 'danger' })
      return
    }
    const options =
      (fieldType === 'select' || fieldType === 'multiselect') && choices.trim()
        ? {
            choices: choices.split(',').map((c) => c.trim()).filter(Boolean).map((c) => ({ value: c, label: c })),
          }
        : null
    setSaving(true)
    try {
      await apiClient.post(base, { label: label.trim(), fieldKey: fieldKey.trim(), fieldType, isRequired: required, options })
      setLabel(''); setFieldKey(''); setFieldType('text'); setRequired(false); setChoices('')
      await refetch()
    } catch (err) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? '')
        : ''
      toast({ title: msg || 'Feld konnte nicht angelegt werden', tone: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  async function deleteField(id: string) {
    try {
      await apiClient.delete(`${base}/${id}`)
      await refetch()
    } catch {
      toast({ title: 'Feld konnte nicht gelöscht werden', tone: 'danger' })
    }
  }

  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 space-y-3">
      <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Formular-Felder</p>

      {(fields ?? []).length > 0 && (
        <div className="space-y-1">
          {(fields ?? []).map((f) => (
            <div key={f.id} className="flex items-center gap-2 bg-white rounded px-2 py-1 text-xs">
              <span className="font-medium text-gray-800 flex-1 truncate">
                {f.label}
                {f.is_required === 1 && <span className="text-red-500"> *</span>}
              </span>
              <code className="text-gray-400">{f.field_key}</code>
              <span className="text-gray-500">{FIELD_TYPE_OPTIONS.find((t) => t.value === f.field_type)?.label}</span>
              <IconButton icon={<Trash2 />} aria-label="Feld löschen" variant="ghost" size="sm" onClick={() => deleteField(f.id)} />
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <Input label="Bezeichnung" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="z.B. Eintrittsdatum" />
        <Input
          label="Schlüssel"
          value={fieldKey}
          onChange={(e) => setFieldKey(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
          placeholder="z.B. start_date"
          hint="Wird als Variable im Kontext verfügbar"
        />
        <Select label="Typ" value={fieldType} onValueChange={setFieldType} options={FIELD_TYPE_OPTIONS} />
        {(fieldType === 'select' || fieldType === 'multiselect') && (
          <Input label="Auswahlmöglichkeiten" value={choices} onChange={(e) => setChoices(e.target.value)} placeholder="a, b, c" hint="Kommagetrennt" />
        )}
        <Switch checked={required} onChange={(e) => setRequired(e.target.checked)} label="Pflichtfeld" />
        <Button variant="secondary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={addField} loading={saving}>
          Feld hinzufügen
        </Button>
      </div>
    </div>
  )
}
